import { useGetWorkflowQuery } from '@/lib/features/workflow/workflowApi'
import {
  RetryValidationResults,
  canRetryFromNode as canRetryFromNodeUtil,
  getAvailableResumeStages as getAvailableResumeStagesUtil,
  getRetryFailureReason as getRetryFailureReasonUtil,
  validateAllNodesForRetry,
} from '@/utils/workflow-utils'
import React, { createContext, useContext, useMemo } from 'react'
import type { NodeExecution, WorkflowRefStrict } from 'sprites-types/api/v2/workflows'

interface RetryValidationContextType {
  validationResults: RetryValidationResults | null
  isLoading: boolean
  canRetryFromNode: (nodeExecutionId: string) => boolean
  getAvailableResumeStages: (nodeExecutionId: string) => Array<'tool_execution' | 'edge_selection'>
  getRetryFailureReason: (nodeExecutionId: string) => string | undefined
}

const RetryValidationContext = createContext<RetryValidationContextType | null>(null)

interface RetryValidationProviderProps {
  children: React.ReactNode
  workflowRef: WorkflowRefStrict
  nodeExecutions: Record<string, NodeExecution>
}

export function RetryValidationProvider({
  children,
  workflowRef,
  nodeExecutions,
}: RetryValidationProviderProps) {
  // Fetch the workflow data
  const { data: workflowData, isLoading: isWorkflowLoading } = useGetWorkflowQuery(workflowRef)

  // Calculate validation results when workflow data changes
  const validationResults = useMemo(() => {
    if (!workflowData?.data || isWorkflowLoading) {
      return null
    }

    return validateAllNodesForRetry(nodeExecutions, workflowData.data)
  }, [workflowData?.data, nodeExecutions, isWorkflowLoading])

  // Helper functions that use the cached validation results
  const canRetryFromNode = (nodeExecutionId: string): boolean => {
    return validationResults ? canRetryFromNodeUtil(nodeExecutionId, validationResults) : false
  }

  const getAvailableResumeStages = (
    nodeExecutionId: string
  ): Array<'tool_execution' | 'edge_selection'> => {
    return validationResults ? getAvailableResumeStagesUtil(nodeExecutionId, validationResults) : []
  }

  const getRetryFailureReason = (nodeExecutionId: string): string | undefined => {
    return validationResults
      ? getRetryFailureReasonUtil(nodeExecutionId, validationResults)
      : undefined
  }

  const contextValue: RetryValidationContextType = {
    validationResults,
    isLoading: isWorkflowLoading,
    canRetryFromNode,
    getAvailableResumeStages,
    getRetryFailureReason,
  }

  return (
    <RetryValidationContext.Provider value={contextValue}>
      {children}
    </RetryValidationContext.Provider>
  )
}

export function useRetryValidation(): RetryValidationContextType | null {
  const context = useContext(RetryValidationContext)
  return context
}

export function useRetryValidationRequired(): RetryValidationContextType {
  const context = useContext(RetryValidationContext)
  if (!context) {
    throw new Error('useRetryValidationRequired must be used within a RetryValidationProvider')
  }
  return context
}
